import { useState } from 'react';
import { DateRangeFilter } from '@/components/date-range-filter';
import type { DatePreset } from '@/lib/date-presets';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Plus, Search, ChevronLeft, ChevronRight, Repeat } from 'lucide-react';

interface Invoice {
  id: string;
  invoiceNumber: number;
  businessProfileName: string;
  accountName: string;
  applicationName: string | null;
  status: string;
  issueDate: string;
  dueDate: string;
  total: number;
  amountPaid: number;
  recurringScheduleId: string | null;
  scheduleName: string | null;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'destructive',
  cancelled: 'secondary',
  void: 'secondary',
};

export function InvoicesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', { page, search, status: statusFilter, dateFrom, dateTo }],
    queryFn: async () => {
      const result = await api.getInvoices({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      return result;
    },
  });

  const invoices = (data?.data || []) as Invoice[];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => navigate('/billing/invoices/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by client or invoice #..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value === 'all' ? '' : value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>
        <DateRangeFilter
          preset={datePreset}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={({ preset, dateFrom: f, dateTo: t }) => {
            setDatePreset(preset);
            setDateFrom(f);
            setDateTo(t);
            setPage(1);
          }}
        />
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No invoices found</h3>
            <p className="text-muted-foreground mb-4">
              {search || statusFilter
                ? 'Try adjusting your filters'
                : 'Create your first invoice to get started'}
            </p>
            {!search && !statusFilter && (
              <Button onClick={() => navigate('/billing/invoices/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <Card
                key={invoice.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-4">
                    <div className="font-mono text-sm text-muted-foreground w-16">
                      #{invoice.invoiceNumber}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{invoice.accountName}</span>
                        {invoice.recurringScheduleId && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"
                            title={
                              invoice.scheduleName
                                ? `Recurring: ${invoice.scheduleName}`
                                : 'Generated from a recurring schedule'
                            }
                          >
                            <Repeat className="h-3 w-3" />
                            Recurring
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {invoice.businessProfileName}
                        {invoice.applicationName && ` / ${invoice.applicationName}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(invoice.total)}</div>
                      {invoice.amountPaid > 0 && invoice.status !== 'paid' && (
                        <div className="text-xs text-muted-foreground">
                          Paid: {formatCurrency(invoice.amountPaid)}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-sm text-muted-foreground w-24">
                      <div>Due {formatDate(invoice.dueDate)}</div>
                    </div>
                    <Badge variant={statusVariant[invoice.status] || 'secondary'} className="w-20 justify-center">
                      {invoice.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, pagination.total)} of{' '}
                {pagination.total} invoices
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
